// @ts-nocheck
import React, { useCallback, useEffect, useRef, useState } from "react";
import DOMPurify from "dompurify";
import { HOME_PAGE_SCOPE, prepareArticleHtml } from "../utils/kbArticleHtml";
import { HOME_MESSAGES, ARTICLE_MESSAGES, classifyArticleResponse } from "../utils/kbArticleFetch";
import { useCitationClicks } from "../utils/useCitationClicks";
import { CitationTwo } from "./CitationTwo";
import environment from "../config/environment";
import "./DashboardHome.css";

/**
 * The Home page (/home) — reached by an employee who has switched Beta Preview on (ACOP-101).
 *
 * Its content is **published from the knowledge base, not built into this app**. That is the point
 * of the card, not an implementation detail: the people who write the page can change it without a
 * code change, a review, a build and a deploy. What used to be here was a card index derived from
 * the side-nav, then a one-line placeholder after that grid was deliberately deleted so this content
 * could replace it without a removal riding inside the change that adds it.
 *
 * -- The division of labour with AVSOS (OS-157) --------------------------------------------------
 *
 * AVSOS assembles the page for the reader's roles and prunes the sections they do not hold **before
 * the bytes leave the server** (OS-157). This component does not filter anything by role, and must
 * not start.
 *
 * The reason is the pruning, not the attributes. Measured against the live staging payload
 * (2026-09-06, as `employee,support`): **0** `data-role` attributes and **13** `data-avs-citation`
 * ones. So whatever strips `data-role` is selective — it is NOT a blanket `ALLOW_DATA_ATTR: false`,
 * which would take `data-avs-citation` with it and break every link on the page. An earlier version
 * of this comment asserted exactly that, which contradicted the characterization test in
 * `copilot-api` two files away; the mechanism lives in AVSOS and is not verifiable from this repo
 * (the `avsight-os` clone here is ~3 months stale and does not contain that code).
 *
 * What matters for THIS component is the observable, and it is enough on its own: the sections the
 * reader is not entitled to **never arrive**, so a `[data-role]` filter here would have nothing to
 * match and would render a page that looked correct to every current reader — all of whom are
 * employees. Client-side role filtering would be theatre. `DashboardHome.test.tsx` asserts the
 * negative, so nobody adds one later.
 *
 * The page also arrives with its stylesheet inlined as a leading <style> and with its links already
 * resolved to `data-avs-citation` + `href="#"`. Both survive the proxy for reasons pinned by
 * characterization tests in copilot-api (`avsOsArticleHelper.test.ts`) -- the anchor loop's
 * `href.startsWith("#")` early return, and the <head> <style> recovery.
 *
 * -- Why the whole payload still goes through prepareArticleHtml ---------------------------------
 *
 * Sanitise, scope the CSS, harden the links, fail closed -- the same treatment the article surfaces
 * get, under HOME_PAGE_SCOPE so the slide-out opening over this page cannot restyle it or be
 * restyled by it. The payload's own CSS is already self-scoped under `.copilot-home` by its author,
 * which makes the scoping a belt-and-braces measure today; it stays because the file being
 * well-behaved now is not a reason to trust the next revision, and the entire feature is that its
 * authors can change it without asking anyone.
 */

/**
 * The one path this page fetches.
 *
 * A module constant, not derived from anything the reader supplies -- but note that is a statement
 * about THIS page, not a security property of the endpoint. `?url=` accepted arbitrary AVSOS-host
 * paths from ACOP-93 until ACOP-102, which added a path allowlist server-side
 * (`isAllowedAvsOsPath`); this value is one of the four families on it, so changing it here is not
 * enough on its own.
 */
const HOME_CONTENT_PATH = "/api/copilot/home/html";

const citationProxyUrl = (path) =>
  environment.BASE_URL + "copilot/avs-os/citation?url=" + encodeURIComponent(path);

/**
 * The shared verdict ladder decides WHAT happened; this re-words it for a landing page.
 * An unrecognised message falls back to `failed` rather than being shown raw (BR-7).
 */
const HOME_MESSAGE_FOR = {
  [ARTICLE_MESSAGES.expired]: HOME_MESSAGES.expired,
  [ARTICLE_MESSAGES.forbidden]: HOME_MESSAGES.forbidden,
  [ARTICLE_MESSAGES.busy]: HOME_MESSAGES.busy,
};

export default function DashboardHome({ onOpenFullArticle }: { onOpenFullArticle?: (urlname: string) => void } = {}) {
  const [html, setHtml] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  /** A reference in the content that goes nowhere. See the onDead note below. */
  const [deadNotice, setDeadNotice] = useState(null);
  /** The citation slide-out, opened over this page. Null when closed. */
  const [citationSource, setCitationSource] = useState(null);
  const contentRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setHtml(null);
    setError(null);

    fetch(citationProxyUrl(HOME_CONTENT_PATH), {
      credentials: "include",
      headers: { "Authorization": "Bearer " + environment.BEARER_TOKEN },
    })
      .then(async (r) => ({
        ok: r.ok,
        status: r.status,
        isJson: (r.headers.get("content-type") || "").includes("application/json"),
        body: await r.text(),
      }))
      .then((response) => {
        if (cancelled) return;

        // The same ladder as the slide-out and the KB page, by construction -- its ordering
        // (envelope 401/403, then 429, then 404) is load-bearing and hard-won. Only the wording
        // differs here.
        const outcome = classifyArticleResponse(response);

        if (outcome.kind === "error") {
          setError(HOME_MESSAGE_FOR[outcome.message] || HOME_MESSAGES.failed);
          setLoading(false);
          return;
        }

        /*
         * A landing page has no "this does not exist" state, so `dead` is a failure here rather
         * than a dead-end page (BR-8). AVSOS makes every refusal a 403 precisely so the 404
         * wording cannot fire -- a 404 on this route means something is wrong, not that the reader
         * asked for something absent.
         */
        if (outcome.kind === "dead") {
          setError(HOME_MESSAGES.failed);
          setLoading(false);
          return;
        }

        const prepared = prepareArticleHtml(outcome.html, DOMPurify.sanitize, HOME_PAGE_SCOPE);

        /*
         * A 200 that carried nothing. prepareArticleHtml returns "" for an empty body, and every
         * branch of the render below is gated -- so without this the reader would get no content,
         * no message and no loading state: the blank page BR-7 rules out. Read as a retrieval
         * failure rather than as an empty page, because from here nothing tells the two apart.
         */
        if (!prepared.html) {
          setError(HOME_MESSAGES.failed);
          setLoading(false);
          return;
        }

        setHtml(prepared.html);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError(HOME_MESSAGES.failed);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  /** A resolved citation opens the slide-out OVER this page, so the reader stays on Home. */
  const onCitation = useCallback((path, label) => {
    setDeadNotice(null);
    setCitationSource({ citationUrl: citationProxyUrl(path), title: label || "Knowledge Base" });
  }, []);

  /*
   * A reference that resolves to nothing.
   *
   * Shown inline rather than in the slide-out because the slide-out is seeded with an ARTICLE and
   * has no dead-entry seed -- faking one by handing it a URL known to fail would be a worse lie
   * than saying so here. This path is also near-unreachable in practice: the real payload carries
   * zero dead markers, and a citation whose target is missing goes through onCitation and lands on
   * the slide-out's own dead page, which is the right treatment. This branch exists for an anchor
   * prepareArticleHtml neutralised, so the click still cannot leave Copilot (BR-5).
   */
  const onDead = useCallback((_marker, label) => {
    setDeadNotice(label || "That reference");
  }, []);

  useCitationClicks({
    containerRef: contentRef,
    html,
    // Home is not an article, but the fragment check still needs a path to compare against, or any
    // fragment-bearing citation navigates instead of scrolling in place.
    currentPath: HOME_CONTENT_PATH,
    onCitation,
    onDead,
  });

  const closeCitation = useCallback(() => setCitationSource(null), []);

  /*
   * "Open full article" is the slide-out's button, shown only when it is handed this callback
   * (CitationTwo.tsx:348). Home has no router of its own, so the owner (Main.tsx, which does)
   * supplies it and performs the SPA route change -- a full page load would drop the embed's
   * `?hideTitle=&email=` query string (9dc3433). Closing the slide-out first leaves Home clean
   * for when the reader comes back. Omitted prop => no button, as before.
   */
  const openFullArticle = useCallback(
    (urlname) => {
      setCitationSource(null);
      onOpenFullArticle?.(urlname);
    },
    [onOpenFullArticle]
  );

  return (
    <div className="container-fluid py-4 px-3" data-testid="home-page">
      {loading && (
        <p className="text-body-secondary small mb-0" data-testid="home-loading">
          Loading&hellip;
        </p>
      )}

      {!loading && error && (
        <p className="text-body-secondary small mb-0" data-testid="home-error">
          {error}
        </p>
      )}

      {deadNotice && (
        <div className="copilot-home-dead-notice" role="status" data-testid="home-dead-notice">
          <span>{deadNotice} is not in the knowledge base.</span>
          <button
            type="button"
            className="copilot-home-dead-dismiss"
            onClick={() => setDeadNotice(null)}
            aria-label="Dismiss"
            data-testid="home-dead-dismiss"
          >
            &times;
          </button>
        </div>
      )}

      {html && (
        <div
          ref={contentRef}
          className="copilot-home-page"
          data-testid="home-content"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      )}

      {citationSource && (
        <CitationTwo
          initialSource={citationSource}
          onClose={closeCitation}
          onOpenFullArticle={onOpenFullArticle ? openFullArticle : undefined}
        />
      )}
    </div>
  );
}
